import type { StoreApi } from 'zustand';
import { createStore } from 'zustand';

import type { BackendNode } from '../api/regeneration-chunks';
import { computeDeselect, computeSelect } from './selection-logic';
import type { DraftClusterRow, DraftFamilyRow } from './table/rows';

export type OnboardingChangesetState = {
    readonly addedRows: readonly DraftFamilyRow[];
    readonly removedIds: ReadonlySet<string>;
    readonly selectedIds: ReadonlySet<string>;
    readonly updatedById: ReadonlyMap<string, BackendNode>;
};

export type OnboardingChangesetActions = {
    readonly applyRegenerationResult: (
        addedRows: readonly DraftFamilyRow[],
        removedIds: ReadonlySet<string>,
        updatedById: ReadonlyMap<string, BackendNode>,
    ) => void;
    readonly clear: () => void;
    readonly removeItem: (id: string) => void;
    readonly toggleSelection: (id: string, derivedRows: readonly DraftFamilyRow[]) => void;
};

export type OnboardingChangesetStore = OnboardingChangesetActions & OnboardingChangesetState;

const collectPendingIds = (
    addedRows: readonly DraftFamilyRow[],
    removedIds: ReadonlySet<string>,
): ReadonlySet<string> => {
    const ids = new Set<string>(removedIds);

    for (const family of addedRows) {
        ids.add(family.id);

        for (const cluster of family.subRows ?? []) {
            ids.add(cluster.id);
        }
    }

    return ids;
};

const initialState: OnboardingChangesetState = {
    addedRows: [],
    removedIds: new Set(),
    selectedIds: new Set(),
    updatedById: new Map(),
};

export const createOnboardingChangesetStore = (): StoreApi<OnboardingChangesetStore> => {
    return createStore<OnboardingChangesetStore>((set) => ({
        ...initialState,

        applyRegenerationResult: (addedRows, removedIds, updatedById) => {
            set({ addedRows, removedIds, selectedIds: collectPendingIds(addedRows, removedIds), updatedById });
        },

        clear: () => set(initialState),

        removeItem: (id) => {
            set((state) => {
                if (state.addedRows.some((f) => f.id === id)) {
                    return { addedRows: state.addedRows.filter((f) => f.id !== id) };
                }

                if (state.addedRows.some((f) => f.subRows?.some((c: DraftClusterRow) => c.id === id))) {
                    return {
                        addedRows: state.addedRows.map((f) => ({
                            ...f,
                            subRows: f.subRows?.filter((c: DraftClusterRow) => c.id !== id),
                        })),
                    };
                }

                return {};
            });
        },

        toggleSelection: (id, derivedRows) => {
            set((state) => {
                const next = state.selectedIds.has(id)
                    ? computeDeselect(state.selectedIds, id, derivedRows)
                    : computeSelect(state.selectedIds, id, derivedRows);

                return { selectedIds: next };
            });
        },
    }));
};
